import { FC } from 'react';

import { Formik, Form, Field } from 'formik';

import { DialogContent, DialogActions, Button, TextField, CircularProgress, Divider } from '@mui/material';

import { SelectedUsersList } from '../SelectedUsersList/SelectedUsersList';
import { BulkTicketFormValues, SelectedUser } from '../types';
import { UsernameInput } from '../UsernameInput/UsernameInput';
import { UserSearch } from '../UserSearch/UserSearch';

import { ticketDistributionSchema } from './validationSchema';

import { UserModel } from 'v2/types/UserModel';

interface BulkDistributionFormProps {
  userSearchQuery: string;
  onUserSearchChange: (query: string) => void;
  usersData: UserModel[];
  isUsersLoading: boolean;
  selectedUsers: SelectedUser[];
  onAddUser: (user: UserModel) => void;
  onAddUsersByTelegram: (users: SelectedUser[]) => void;
  onRemoveUser: (userId: string) => void;
  isDistributing: boolean;
  isValidating: boolean;
  onSubmit: (values: BulkTicketFormValues) => void;
  onClose: () => void;
  onValidateUsernames: (usernames: string[]) => Promise<UserModel[]>;
}

export const BulkDistributionForm: FC<BulkDistributionFormProps> = ({
  userSearchQuery,
  onUserSearchChange,
  usersData,
  isUsersLoading,
  selectedUsers,
  onAddUser,
  onAddUsersByTelegram,
  onRemoveUser,
  isDistributing,
  isValidating,
  onSubmit,
  onClose,
  onValidateUsernames,
}) => {
  return (
    <Formik initialValues={{ users: [], quantity: 1 }} validationSchema={ticketDistributionSchema} onSubmit={onSubmit}>
      {({ errors, touched }) => (
        <Form>
          <DialogContent>
            <UserSearch
              searchQuery={userSearchQuery}
              onSearchChange={onUserSearchChange}
              users={usersData}
              isLoading={isUsersLoading}
              selectedUsers={selectedUsers}
              onUserSelect={onAddUser}
            />

            <Divider sx={{ my: 3 }} />

            <UsernameInput
              selectedUsers={selectedUsers}
              onAddUsers={onAddUsersByTelegram}
              onValidateUsers={onValidateUsernames}
              isValidating={isValidating}
            />

            <Divider sx={{ my: 3 }} />

            <SelectedUsersList users={selectedUsers} onRemoveUser={onRemoveUser} isDistributing={isDistributing} />

            <Field name="quantity">
              {({ field }: any) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Number of tickets per user"
                  error={touched.quantity && Boolean(errors.quantity)}
                  helperText={touched.quantity && errors.quantity}
                  inputProps={{ min: 1, max: 100 }}
                />
              )}
            </Field>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose} disabled={isDistributing}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isDistributing || selectedUsers.length === 0}
              startIcon={isDistributing && <CircularProgress size={20} />}
            >
              {isDistributing ? 'Distributing...' : 'Distribute Tickets'}
            </Button>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};
